import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { AttachmentsSection } from './AttachmentsSection'
import { FiLoader, FiArrowUpCircle, FiArrowDownCircle, FiPaperclip, FiX, FiChevronDown, FiPlus, FiUserPlus, FiArrowLeft, FiClipboard, FiSave, FiAlignLeft, FiCheck } from 'react-icons/fi'
import { type Kind, type Opt, type Txn, type SplitRow, LABELS, METHODS, inputCls, inputBase, norm, paraNumero, novoUid, resolverConta } from './finance-shared'
import { SplitCatSelect } from './SplitCatSelect'
import { RateioCategoriaBox } from './RateioCategoriaBox'
import ConfirmDialog from './ConfirmDialog'

// Valor de uma parte do rateio, já resolvendo a conta ("50+300" = 350) mesmo
// antes do Enter. Sem isso a faixa de soma lia 50300 enquanto ele digitava e
// mostrava "passou" — e o salvar gravaria esse número.
const valorParte = (v: string) => paraNumero(resolverConta(v))

// PLANOS (21/10/2026): campos de uma linha com altura fixa h-9 (36px) e rótulo
// 13px — a gaveta virou seções de papel (.pln-secao) e os campos precisavam
// da mesma métrica em todas elas. Textarea continua com inputCls puro.
const campo = `${inputCls} h-9`
const rotulo = 'block text-[13px] font-medium text-foreground mb-1.5'
// Ações internas das seções (rateio, colar, repetir): mesma altura dos campos.
const chip = 'neu-convex inline-flex items-center gap-1.5 h-9 text-sm rounded-lg border border-border px-3 hover:bg-secondary disabled:opacity-40'

// Modal de criar/editar lançamento (despesa/receita) — §5.4/§5.5. Extraído do
// Contas.tsx pra ser reusável na "Nova ▾" da Visão de competência e na ação
// global "Novo registro" (§5.11). `txn` presente = edição; ausente = criação.

// Subconjunto do item do GET /api/transactions que o autocomplete de descrição
// reaproveita — só os campos que fazem sentido SUGERIR (valor e datas ficam de
// fora de propósito: mudam a cada lançamento).
type DescSug = {
  description: string; party_name: string | null; party_doc: string | null
  category_id: number | null; category_name: string | null
  cost_center_id: number | null; payment_method: string | null
}

// `defaults` (só na criação) abre o modal PRÉ-PREENCHIDO — usado pela prova
// real do Extrato: linha do banco sem par vira receita/despesa em 1 clique
// (transferência não serve quando a contraparte é externa, ex. PIX recebido).
export function TxnModal({ kind, txn, defaults, onClose, onSaved, onCreated }: {
  kind: Kind; txn?: Txn
  // `category_id`/`party_name` ampliam o pré-preenchimento (conciliação);
  // `lockPaid` esconde os toggles Já pago/Agendado — usado quando a baixa vem
  // de fora (conciliação bancária dá a baixa com a data do movimento).
  defaults?: {
    description?: string; amount?: number; date?: string; bank_account_id?: number; paid?: boolean
    category_id?: number; party_name?: string; lockPaid?: boolean
    // criação lastreada num movimento de banco (conciliação / prova real do
    // extrato): o backend afrouxa o guard anti-duplicata (o banco pode ter
    // linhas iguais no mesmo dia). Sem isto, a criação e tratada como avulsa
    // (Contas) e a duplicata e bloqueada de forma dura.
    from_bank_movement?: boolean
  }
  onClose: () => void; onSaved: () => void
  // Na criação, se presente, recebe os ids criados NO LUGAR de onSaved —
  // deixa o chamador encadear um passo (ex.: conciliar) antes de recarregar.
  onCreated?: (ids: number[]) => void
}) {
  const { authenticatedFetch } = useAuth()
  const L = LABELS[kind]
  const editing = !!txn
  const today = new Date().toISOString().slice(0, 10)

  const [cats, setCats] = useState<Opt[]>([])
  const [ccs, setCcs] = useState<Opt[]>([])
  const [accs, setAccs] = useState<Opt[]>([])

  const [description, setDescription] = useState(txn?.description || defaults?.description || '')
  const [party, setParty] = useState(txn?.party_name || defaults?.party_name || '')
  // Documento (CPF/CNPJ) do favorecido escolhido no autocomplete. Seedado do txn
  // na edição pra não zerar o party_doc já salvo (o PUT sobrescreve o campo).
  // Editar o texto à mão limpa — doc não pode ficar órfão de outro nome.
  const [partyDoc, setPartyDoc] = useState(txn?.party_doc || '')
  // `category` vem junto da API — usada pra puxar a categoria do lançamento
  // automaticamente quando o favorecido tem uma cadastrada (pedido do diretor).
  const [favSugs, setFavSugs] = useState<{ id: number; name: string; document: string | null; category: string | null }[]>([])
  const [favOpen, setFavOpen] = useState(false)
  // Cadastro rápido de favorecido SEM sair do lançamento (pedido do diretor
  // 2026-07-21). Quando != null, o drawer mostra o formulário curto no lugar do
  // lançamento — o TxnModal continua montado, então tudo que já foi digitado na
  // despesa (valor, competência, categoria, anexos escolhidos) sobrevive. Só os
  // 4 campos que o lançamento consome; o resto (PIX, banco, contato) se completa
  // depois em Cadastros → Favorecidos.
  const [favForm, setFavForm] = useState<{ name: string; person_type: string; document: string; category: string } | null>(null)
  const [favSaving, setFavSaving] = useState(false)
  const [favErr, setFavErr] = useState('')
  // true = não buscar (mount com party pré-preenchido, ou logo após selecionar);
  // só vira false quando o USUÁRIO digita.
  const favSkipRef = useRef(true)
  // Autocomplete de descrição (só na criação): sugere lançamentos anteriores
  // do mesmo tipo pra reaproveitar o preenchimento repetitivo (aluguel, energia…).
  const [descSugs, setDescSugs] = useState<DescSug[]>([])
  const [descOpen, setDescOpen] = useState(false)
  // mesmo papel do favSkipRef: mount pré-preenchido/seleção não disparam busca
  const descSkipRef = useRef(true)
  // Combobox de categoria (~110 opções tornavam o <select> penoso de rolar).
  // `catQuery` só vale enquanto ABERTO; fechado, o input exibe o nome da
  // selecionada derivado de `cats` — isso cobre a edição, onde cats chega
  // async e o nome aparece sozinho quando a lista carrega.
  const [catQuery, setCatQuery] = useState('')
  const [catOpen, setCatOpen] = useState(false)
  const catInputRef = useRef<HTMLInputElement>(null)
  const [amount, setAmount] = useState(txn ? String(txn.amount) : defaults?.amount != null ? String(defaults.amount) : '')
  const [competence, setCompetence] = useState(txn?.competence_date || defaults?.date || today)
  const [due, setDue] = useState(txn?.due_date || defaults?.date || today)
  const [categoryId, setCategoryId] = useState<string>(txn ? String(txn.category_id) : defaults?.category_id ? String(defaults.category_id) : '')

  // Rateio (2026-07-22): a fatura do cartão é um débito só, composto de várias
  // despesas. As partes ficam em transaction_split; a fatura segue sendo este
  // lançamento, que é o que concilia com o extrato.
  // (O tipo SplitRow subiu para finance-shared.ts em 2026-07-30 — o
  // RateioCategoriaBox devolve linhas nesse formato.)
  const [splits, setSplits] = useState<SplitRow[]>([])
  const [splitsOpen, setSplitsOpen] = useState(false)
  // Colar do Excel: o diretor já separa as categorias de gasto numa planilha;
  // cola aqui a lista pronta (categoria + valor por linha) em vez de adicionar
  // parte a parte. Casa a categoria por nome; não reconhecida vira linha com o
  // nome pra ele escolher a categoria. Substitui as partes atuais (é a lista dele).
  // Índices das partes com o campo de descrição ABERTO à mão. A descrição não
  // fica visível por padrão: com 7 partes eram 14 campos empilhados, e numa
  // fatura de cartão (19 partes) seriam 38. Ela aparece sozinha só onde
  // importa — quando já tem texto, ou quando a parte está sem categoria.
  const [descAberta, setDescAberta] = useState<Set<string>>(new Set())
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [pasteMsg, setPasteMsg] = useState('')
  const aplicarColagem = () => {
    const rows: SplitRow[] = []
    const naoRec: string[] = []
    const stop = new Set(['e', 'de', 'do', 'da', 'das', 'dos', 'com', 'para', 'por', 'em'])
    const toks = (s: string) => norm(s).split(/[^a-z0-9]+/).filter(w => w && !stop.has(w))
    for (const raw of pasteText.split(/\r?\n/)) {
      const l = raw.trim(); if (!l) continue
      const cols = l.includes('\t') ? l.split('\t') : l.includes(';') ? l.split(';') : l.split(/\s{2,}/)
      if (cols.length < 2) continue
      const nome = cols[0].trim()
      let v = cols[cols.length - 1].replace(/[^\d.,-]/g, '')
      if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.') // vírgula = decimal; ponto = milhar
      const val = Number(v)
      if (!nome || isNaN(val)) continue
      let cat = cats.find(c => norm(c.name) === norm(nome))
        || cats.find(c => norm(c.name).includes(norm(nome)) || norm(nome).includes(norm(c.name)))
      if (!cat) {
        // 3ª tentativa: todas as palavras da colagem numa (e só uma) categoria —
        // pega "Telefonia e Internet" → "Telefonia, Internet e Movél". Se casar em
        // mais de uma, NÃO adivinha: deixa vazio pro usuário escolher.
        const qt = toks(nome)
        if (qt.length) {
          const cand = cats.filter(c => { const ct = new Set(toks(c.name)); return qt.every(w => ct.has(w)) })
          if (cand.length === 1) cat = cand[0]
        }
      }
      if (!cat) naoRec.push(nome)
      rows.push({ uid: novoUid(), category_id: cat ? String(cat.id) : '', amount: val.toFixed(2).replace('.', ','), description: cat ? '' : nome })
    }
    if (!rows.length) { setPasteMsg('Nada reconhecido. Use uma linha por parte: categoria [tab] valor.'); return }
    setSplits(rows); setSplitsOpen(true); setPasteOpen(false); setPasteText('')
    setPasteMsg(`${rows.length} parte(s) coladas${naoRec.length ? ` · ${naoRec.length} sem categoria reconhecida (${naoRec.slice(0, 4).join(', ')}${naoRec.length > 4 ? '…' : ''}) — selecione manualmente.` : '.'}`)
  }
  const [costCenterId, setCostCenterId] = useState<string>(txn?.cost_center_id ? String(txn.cost_center_id) : '')
  const [accountId, setAccountId] = useState<string>(txn?.bank_account_id ? String(txn.bank_account_id) : defaults?.bank_account_id ? String(defaults.bank_account_id) : '')
  const [method, setMethod] = useState<string>(txn?.payment_method || '')
  const [reference, setReference] = useState(txn?.reference_code || '')
  const [notes, setNotes] = useState(txn?.notes || '')
  const [paid, setPaid] = useState(txn ? txn.status === 'pago' : !!defaults?.paid)
  const [scheduled, setScheduled] = useState(txn?.status === 'agendado')
  // recorrência/parcelamento (só na criação)
  const [repeatMode, setRepeatMode] = useState<'none' | 'installments' | 'recurrence'>('none')
  const [repeatCount, setRepeatCount] = useState(2)

  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Anexos na CRIAÇÃO (pedido do diretor 2026-07-10 — a foto da fatura importa
  // em todas as etapas): os arquivos ficam pendentes no modal e sobem pro
  // lançamento recém-criado logo após o POST (a API de anexos exige entity_id).
  // Na edição segue o AttachmentsSection (upload direto).
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  useEffect(() => {
    (async () => {
      const [rc, rcc, ra] = await Promise.all([
        authenticatedFetch(`/api/categories?kind=${kind}`),
        authenticatedFetch('/api/cost-centers?status=active&pageSize=100'),
        authenticatedFetch('/api/bank-accounts'),
      ])
      const [dc, dcc, da] = await Promise.all([rc.json(), rcc.json(), ra.json()])
      // opens_split precisa sobreviver ao map: é ele que decide se o quadro de
      // rateio abre sozinho. Reduzir a categoria a {id,name} aqui fazia o campo
      // chegar da API e morrer no caminho, sem erro nenhum.
      if (dc.success) setCats(dc.categories.map((c: any) => ({ id: c.id, name: c.name, opens_split: c.opens_split })))
      if (dcc.success) setCcs(dcc.items.map((c: any) => ({ id: c.id, name: c.name })))
      if (da.success) setAccs(da.items.map((a: any) => ({ id: a.id, name: a.name })))
    })().catch(() => {})
  }, [authenticatedFetch, kind])

  // Na edição, traz o rateio já gravado (e abre o quadro se houver).
  // Conciliação do lançamento em edição (vem do GET /:id/splits, que olha as
  // DUAS representações — a 1:1 e o grupo N<->M). Serve só para AVISAR: mexer
  // no rateio não desfaz conciliação nenhuma, porque o que casa com o banco é
  // transaction.amount, que o rateio não toca.
  const [conc, setConc] = useState<{ conciliado: boolean; em: string | null } | null>(null)

  // Só vira true quando o GET do rateio REALMENTE respondeu. É o que autoriza
  // mandar `splits` no corpo do PUT.
  //
  // Sem esta trava havia perda de dado: a edição manda `body.splits` sempre, e
  // o backend trata lista vazia como "apaguei o rateio" (é gravação legítima).
  // Se o GET falhasse, demorasse, ou o usuário salvasse antes de ele voltar, o
  // estado ainda estaria vazio e as partes do lançamento seriam APAGADAS — as
  // 19 de uma fatura de cartão sumiriam por causa de uma requisição lenta.
  // Com a trava, nesse caso o `splits` simplesmente não vai no corpo e o
  // backend preserva o que já existe.
  const splitsCarregadosRef = useRef(false)

  useEffect(() => {
    if (!txn?.id) return
    splitsCarregadosRef.current = false
    authenticatedFetch(`/api/transactions/${txn.id}/splits`).then(r => r.json()).then(d => {
      if (!d.success) return
      setConc({ conciliado: !!d.conciliado, em: d.conciliado_em || null })
      splitsCarregadosRef.current = true
      if (!d.items?.length) return
      setSplits(d.items.map((i: any) => ({
        uid: novoUid(),
        category_id: i.category_id ? String(i.category_id) : '',
        amount: String(i.amount), description: i.description || '',
        category_name: i.category_name || undefined,
      })))
      setSplitsOpen(true)
    }).catch(() => {})
  }, [authenticatedFetch, txn?.id])

  // Autocomplete de favorecidos: busca server-side (name/document/category) com
  // debounce. Falha é silenciosa — o campo continua funcionando como texto livre.
  useEffect(() => {
    if (favSkipRef.current) return
    const q = party.trim()
    if (q.length < 2) { setFavSugs([]); setFavOpen(false); return }
    const t = setTimeout(async () => {
      try {
        const r = await authenticatedFetch(`/api/favorecidos?status=active&search=${encodeURIComponent(q)}&pageSize=8`)
        const d = await r.json()
        // Abre mesmo com 0 resultados: a lista vazia é onde mora o "cadastrar
        // este aqui". Antes ela simplesmente não aparecia e o beco era sem saída.
        if (d.success) { setFavSugs(d.items); setFavOpen(true) }
      } catch { /* autocomplete é conveniência, não bloqueia o cadastro */ }
    }, 250)
    return () => clearTimeout(t)
  }, [party, authenticatedFetch])

  const pickFav = (f: { name: string; document: string | null; category?: string | null }) => {
    favSkipRef.current = true // preencher via seleção não deve reabrir a busca
    setParty(f.name)
    setPartyDoc(f.document || '')
    // Categoria automática: se o lançamento ainda está SEM categoria e o
    // favorecido tem uma cadastrada que casa (sem acento/caixa) com uma
    // categoria financeira deste tipo, seleciona sozinho. Só sugere — nunca
    // sobrescreve escolha já feita pelo usuário ou pelos defaults.
    if (!categoryId && f.category) {
      const m = cats.find(c => norm(c.name) === norm(f.category!))
      if (m) setCategoryId(String(m.id))
    }
    setFavOpen(false)
  }

  // Abre o cadastro rápido já com o que a pessoa digitou no campo — ela não
  // redigita o nome que acabou de escrever e não achou.
  const openFavForm = () => {
    setFavOpen(false)
    setFavErr('')
    setFavForm({ name: party.trim(), person_type: 'J', document: '', category: '' })
  }

  // Salva e JÁ USA: volta pro lançamento com o campo preenchido, passando pelo
  // mesmo pickFav do autocomplete — então a categoria automática vale igual,
  // sem uma segunda regra pra manter em sincronia.
  const saveFav = async () => {
    const nome = favForm?.name.trim()
    if (!nome) { setFavErr('Nome é obrigatório'); return }
    setFavSaving(true); setFavErr('')
    try {
      const r = await authenticatedFetch('/api/favorecidos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...favForm, name: nome }),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'erro ao criar favorecido')
      pickFav({ name: nome, document: favForm!.document || null, category: favForm!.category || null })
      setFavForm(null)
    } catch (e: any) {
      setFavErr(e.message || 'erro ao criar favorecido')
    } finally {
      setFavSaving(false)
    }
  }

  // Autocomplete de descrição (só na CRIAÇÃO — na edição a descrição já existe
  // e sugerir outro lançamento só confundiria). Reusa a busca da lista
  // (description/party_name); dir=desc = vencimentos recentes primeiro, o
  // hábito atual pesa mais que o histórico antigo.
  useEffect(() => {
    if (editing || descSkipRef.current) return
    const q = description.trim()
    if (q.length < 2) { setDescSugs([]); setDescOpen(false); return }
    const t = setTimeout(async () => {
      try {
        const r = await authenticatedFetch(`/api/transactions?kind=${kind}&search=${encodeURIComponent(q)}&pageSize=8&dir=desc`)
        const d = await r.json()
        if (d.success) {
          // dedup por descrição normalizada — o mesmo aluguel aparece 1x só
          const seen = new Set<string>()
          const uniq: DescSug[] = []
          for (const it of d.items as DescSug[]) {
            const k = norm(it.description || '')
            if (!k || seen.has(k)) continue
            seen.add(k); uniq.push(it)
          }
          setDescSugs(uniq); setDescOpen(uniq.length > 0)
        }
      } catch { /* conveniência — a digitação livre continua valendo */ }
    }, 250)
    return () => clearTimeout(t)
  }, [description, editing, kind, authenticatedFetch])

  // Reaproveita um lançamento anterior: preenche a descrição e SUGERE o resto —
  // só entra no que estiver VAZIO (não sobrescreve o que o usuário/defaults já
  // puseram). Valor e datas nunca são copiados: mudam a cada lançamento.
  const pickDesc = (s: DescSug) => {
    descSkipRef.current = true
    setDescription(s.description)
    if (!categoryId && s.category_id) setCategoryId(String(s.category_id))
    if (!costCenterId && s.cost_center_id) setCostCenterId(String(s.cost_center_id))
    if (!method && s.payment_method) setMethod(s.payment_method)
    if (!party.trim() && s.party_name) {
      favSkipRef.current = true // preenchimento programático não abre a busca de favorecidos
      setParty(s.party_name)
      setPartyDoc(s.party_doc || '')
    }
    setDescOpen(false)
  }

  // Nome da categoria selecionada (mostrado no combobox fechado) e lista
  // filtrada pelo texto digitado — filtro client-side, cats já está na memória.
  const selCatName = cats.find(c => String(c.id) === categoryId)?.name || ''
  // Quem decide se o rateio abre sozinho é a CATEGORIA, via `opens_split`
  // (marcado em Categorias). Antes era regex no nome (/cart[ãa]o/): resolvia a
  // fatura de cartão e só ela — JVN SERVIÇOS é o mesmo problema (um pagamento
  // que é a soma de vários serviços) e jamais casaria com "cartão".
  // A regex fica como REDE: categoria de cartão criada hoje e ainda não
  // marcada continua abrindo, então trocar o critério não regride nada.
  // Em nenhum dos casos isto obriga rateio — o quadro segue disponível e
  // podendo ficar vazio em qualquer despesa.
  const catSel = cats.find(c => String(c.id) === categoryId)
  const pareceCartao = catSel?.opens_split === 1 || /cart[ãa]o/i.test(selCatName)
  // O comentário acima prometia "disponível em qualquer despesa", mas até
  // 2026-10-16 não havia como ABRIR o quadro fora do cartão/JVN: o "Adicionar
  // parte" morava dentro dele. Agora qualquer despesa ganha o botão "Dividir
  // em partes" (pedido do diretor: marketing lançado junto com o pagamento das
  // líderes). Backend, DRE e Fluxo de Caixa já aceitavam qualquer categoria.
  const rateioAberto = pareceCartao || splitsOpen || splits.length > 0
  const totalLancamento = paraNumero(amount)
  const rateado = splits.reduce((a, r) => a + valorParte(r.amount), 0)
  const sobra = Math.round((totalLancamento - rateado) * 100) / 100

  // Partes no formato da API. Na EDIÇÃO elas vão JUNTO no PUT do lançamento
  // (atômico); só na criação viram uma segunda chamada, porque o id só existe
  // depois do POST.
  const splitsParaApi = () => splits
    .filter(r => valorParte(r.amount) > 0)
    .map(r => ({ category_id: r.category_id || null,
      amount: valorParte(r.amount),
      description: r.description || null }))

  const salvarSplits = async (id: number) => {
    const items = splits
      .filter(r => valorParte(r.amount) > 0)
      .map(r => ({ category_id: r.category_id || null,
        amount: valorParte(r.amount),
        description: r.description || null }))
    // Lista vazia é gravação legítima: significa "apaguei o rateio".
    const r = await authenticatedFetch(`/api/transactions/${id}/splits`, {
      method: 'PUT', body: JSON.stringify({ items }),
    })
    const d = await r.json()
    if (!d.success) throw new Error(d.error || 'falha ao salvar o rateio')
  }

  // "Salvar como modelo" (2026-07-30): fecha o laço da caixa de rateio da
  // categoria. Sem isto, montar o modelo exigiria digitar tudo de novo — que é
  // exatamente a queixa que originou a feature. Manda a lista inteira de uma
  // vez (PUT em lote) pra não deixar meio modelo gravado se algo falhar.
  // `modeloVersao` avisa o RateioCategoriaBox a recarregar.
  const [modeloVersao, setModeloVersao] = useState(0)
  const [modeloMsg, setModeloMsg] = useState('')
  // Quantas partes o modelo da categoria já tem (vem do RateioCategoriaBox).
  // Importa porque o PUT em lote SUBSTITUI o modelo inteiro — salvar por cima
  // de um modelo em percentual o converteria em valores fixos deste mês, sem
  // volta. Por isso confirma quando já existe conteúdo.
  const [modeloCount, setModeloCount] = useState(0)
  const [modeloSalvando, setModeloSalvando] = useState(false)
  const [confirmModelo, setConfirmModelo] = useState(false)
  const salvarComoModelo = async () => {
    const items = splits
      .filter(r => r.category_id || valorParte(r.amount) > 0)
      .map(r => ({
        target_category_id: r.category_id ? Number(r.category_id) : null,
        // Grava o VALOR de hoje. Percentual é escolha consciente do usuário na
        // própria caixa do modelo — adivinhar percentual a partir de um mês só
        // seria inventar uma regra que ninguém pediu.
        amount: valorParte(r.amount) || null,
        description: r.description || null,
      }))
    if (!items.length) { setModeloMsg('Nada para salvar: preencha ao menos uma parte.'); return }
    setModeloMsg(''); setModeloSalvando(true); setConfirmModelo(false)
    try {
      const r = await authenticatedFetch(`/api/categories/${categoryId}/split-template`, {
        method: 'PUT', body: JSON.stringify({ items }),
      })
      const d = await r.json()
      if (!d.success) { setModeloMsg(d.error || 'Falha ao salvar o modelo.'); return }
      setModeloVersao(v => v + 1)
      setModeloMsg(`Modelo da categoria atualizado com ${d.linhas} parte(s).`)
    } catch { setModeloMsg('Erro de conexão ao salvar o modelo.') }
    finally { setModeloSalvando(false) }
  }
  const catFiltered = catQuery.trim() ? cats.filter(c => norm(c.name).includes(norm(catQuery))) : cats

  const save = async () => {
    if (!description.trim()) { setErr('Informe a descrição.'); return }
    if (!(Number(amount) > 0)) { setErr('Informe um valor maior que zero.'); return }
    if (!categoryId) { setErr('Selecione a categoria.'); return }
    setSaving(true); setErr(null)
    const body: any = {
      description, party_name: party, party_doc: partyDoc || null, amount: Number(amount),
      competence_date: competence, due_date: due,
      category_id: Number(categoryId),
      cost_center_id: costCenterId ? Number(costCenterId) : null,
      bank_account_id: accountId ? Number(accountId) : null,
      payment_method: method || null, reference_code: reference,
      // ao editar um lançamento já pago, preserva a data de pagamento ORIGINAL
      // (o modal não tem campo de data de pagamento); só cai no vencimento quando
      // é uma baixa nova. Evita corromper o regime de caixa (fluxo/DRE/extrato-pj).
      notes, paid, scheduled, payment_date: paid ? (txn?.payment_date || due) : null,
    }
    if (!editing) {
      if (repeatMode === 'installments') body.installments = Math.max(2, repeatCount)
      else if (repeatMode === 'recurrence') body.recurrence = { count: Math.max(2, repeatCount) }
      if (defaults?.from_bank_movement) body.from_bank_movement = true
    } else if (splitsCarregadosRef.current) {
      // Rateio vai JUNTO na edição: valor e partes gravam na mesma transação,
      // então dá para reduzir o valor e ajustar as partes de uma vez só.
      // Lista vazia é gravação legítima AQUI: significa "apaguei o rateio" —
      // por isso só entra depois que o GET confirmou o que existia. Omitir a
      // chave faz o backend preservar as partes atuais.
      body.splits = splitsParaApi()
    }
    try {
      let r = editing
        ? await authenticatedFetch(`/api/transactions/${txn!.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await authenticatedFetch(`/api/transactions?kind=${kind}`, { method: 'POST', body: JSON.stringify(body) })
      let d = await r.json()
      // Guardrail anti-duplicata do backend (2026-07-22): 409 quando já existe
      // lançamento com a mesma conta, data, valor e tipo. Não é bloqueio — dois
      // pagamentos iguais no mesmo dia acontecem; quem decide é quem está aqui.
      if (r.status === 409 && d?.duplicado) {
        if (d.bloqueado) {
          // criação avulsa (Contas) que duplica algo existente: bloqueio DURO,
          // sem "criar assim mesmo" — o certo e Editar o lançamento existente.
          setErr(d.error || 'Já existe um lançamento igual. Para alterá-lo, use Editar em vez de criar outro.')
          return
        }
        if (!confirm(`${d.error}\n\nLançar assim mesmo?`)) { setErr(null); return }
        r = await authenticatedFetch(`/api/transactions?kind=${kind}`, {
          method: 'POST', body: JSON.stringify({ ...body, confirmar_duplicado: true }),
        })
        d = await r.json()
      }
      if (!d.success) { setErr(d.error || 'Falha ao salvar'); return }
      // Sobe os anexos pendentes pro lançamento criado (na série/parcelamento,
      // vão pra 1ª ocorrência). Falha de anexo NÃO desfaz o lançamento — avisa.
      if (!editing && pendingFiles.length) {
        const newId = Array.isArray(d.ids) ? d.ids[0] : null
        if (newId) {
          let falhas = 0
          for (const file of pendingFiles) {
            try {
              const fd = new FormData()
              fd.append('file', file); fd.append('entity_type', 'transaction'); fd.append('entity_id', String(newId))
              const ar = await authenticatedFetch('/api/attachments', { method: 'POST', body: fd })
              const ad = await ar.json()
              if (!ad.success) falhas++
            } catch { falhas++ }
          }
          if (falhas) alert(`Lançamento criado, mas ${falhas} anexo${falhas === 1 ? '' : 's'} falhou${falhas === 1 ? '' : 'ram'} ao subir — abra o lançamento e anexe de novo.`)
        }
      }
      // Na EDIÇÃO o rateio já foi junto no PUT (campo `splits` do corpo), numa
      // só transação. Isso é o que permite REDUZIR o valor e corrigir as partes
      // no mesmo salvamento: em duas chamadas, o PUT do lançamento batia na
      // guarda de rateio, tomava 409 e abortava antes de mandar as partes.
      //
      // Na CRIAÇÃO continua sendo uma segunda chamada, porque o id só existe
      // depois do POST. Falha aqui NÃO desfaz o lançamento — avisa e deixa
      // tentar de novo na edição, mesmo critério já usado pelos anexos.
      if (!editing) {
        const idSalvo = Array.isArray(d.ids) ? d.ids[0] : null
        if (idSalvo && splits.length > 0) {
          try { await salvarSplits(idSalvo) }
          catch (e: any) { setErr(`Lançamento salvo, mas o rateio falhou: ${e.message}`); setSaving(false); return }
        }
      }

      if (!editing && onCreated) onCreated(Array.isArray(d.ids) ? d.ids : [])
      else onSaved()
    } catch { setErr('Erro de conexão') }
    finally { setSaving(false) }
  }

  return (
    <SideModal
      title={favForm ? 'Novo favorecido' : (editing ? `Editar ${L.noun}` : L.newBtn)}
      subtitle={favForm
        ? 'Cadastro rápido — ao salvar, volta pro lançamento com o campo preenchido'
        : (kind === 'despesa' ? 'Obrigação a pagar' : 'Receita a receber')}
      icon={favForm ? FiUserPlus : (kind === 'despesa' ? FiArrowUpCircle : FiArrowDownCircle)}
      onClose={onClose}
      footer={favForm ? (
        <>
          {/* "Voltar", não "Cancelar": o lançamento continua vivo atrás — dizer
              cancelar sugeriria que fechar aqui descarta o que já foi digitado. */}
          <button onClick={() => setFavForm(null)}
            className="neu-tab inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm text-muted-foreground hover:bg-secondary">
            <FiArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <button onClick={saveFav} disabled={favSaving}
            className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {favSaving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar e usar
          </button>
        </>
      ) : (
        <>
          <button onClick={onClose} className="neu-tab h-9 px-3 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </>
      )}
    >
      {/* Cadastro rápido de favorecido. Só troca o que o drawer DESENHA — o
          TxnModal segue montado, então o lançamento inteiro (inclusive anexos já
          escolhidos) continua no estado, intacto, esperando o "Voltar". */}
      {/* PLANOS (21/10/2026): o cadastro rápido é UMA seção de papel; o erro
          fica fora dela, acima, como no lançamento. */}
      {favForm && favErr && <div className="text-sm text-red-500">{favErr}</div>}
      {favForm && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Dados do favorecido</h4>
          <div>
            <label className={rotulo}>Nome*</label>
            <input autoFocus value={favForm.name}
              onChange={e => setFavForm({ ...favForm, name: e.target.value })}
              className={campo} placeholder="Ex.: Fornecedor X Ltda" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={rotulo}>Tipo</label>
              <select value={favForm.person_type}
                onChange={e => setFavForm({ ...favForm, person_type: e.target.value })} className={campo}>
                <option value="J">Jurídica</option>
                <option value="F">Física</option>
              </select>
            </div>
            <div>
              <label className={rotulo}>CPF/CNPJ</label>
              <input value={favForm.document}
                onChange={e => setFavForm({ ...favForm, document: e.target.value })} className={campo} />
            </div>
          </div>
          <div>
            <label className={rotulo}>Categoria</label>
            {/* Lista as categorias DESTE tipo de lançamento (já carregadas aqui).
                Escolher uma liga o preenchimento automático da categoria quando
                este favorecido for usado — o mesmo casamento por nome do pickFav. */}
            <select value={favForm.category}
              onChange={e => setFavForm({ ...favForm, category: e.target.value })} className={campo}
              title="Opcional — preenche sozinha a categoria do lançamento nas próximas vezes">
              <option value="">— sem categoria —</option>
              {cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <p className="text-[13px] text-muted-foreground">
            PIX, dados bancários e contato ficam pra completar depois em Cadastros → Favorecidos.
          </p>
        </section>
      )}

      {!favForm && <>
      {err && <div className="text-sm text-red-500">{err}</div>}

      {/* PLANOS (21/10/2026): o formulário era uma pilha só de campos soltos.
          Agora são seções de papel (.pln-secao, no index.css) sobre o chão da
          gaveta, que já espaça os filhos (space-y-4). Ordem e campos iguais. */}
      <section className="pln-secao">
          <h4 className="pln-secao-titulo">O lançamento</h4>
          <div className="relative">
            <label className={rotulo}>Descrição*</label>
            <input autoFocus value={description} autoComplete="off"
              title={editing ? undefined : 'Digite 2+ letras para sugerir lançamentos anteriores'}
              onChange={e => { descSkipRef.current = false; setDescription(e.target.value) }}
              onKeyDown={e => { if (e.key === 'Escape' && descOpen) { e.stopPropagation(); setDescOpen(false) } }}
              onBlur={() => setTimeout(() => setDescOpen(false), 150)}
              className={campo} placeholder="Ex.: Aluguel do escritório" />
            {!editing && descOpen && descSugs.length > 0 && (
              <ul className="neu-pop absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg glass-popover">
                {descSugs.map((s, i) => (
                  <li key={`${s.description}-${i}`}>
                    {/* onMouseDown previne o blur do input antes do onClick disparar */}
                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickDesc(s)}
                      title="Usar esta descrição e sugerir categoria, centro de custo, forma e fornecedor (só preenche o que estiver vazio)"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-secondary">
                      <span className="block text-foreground truncate">{s.description}</span>
                      {s.category_name && <span className="block text-xs text-muted-foreground truncate">{s.category_name}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <label className={rotulo}>{L.party}</label>
              <input value={party} autoComplete="off"
                title="Digite 2+ letras para buscar nos favorecidos cadastrados"
                onChange={e => { favSkipRef.current = false; setParty(e.target.value); setPartyDoc('') }}
                onKeyDown={e => { if (e.key === 'Escape' && favOpen) { e.stopPropagation(); setFavOpen(false) } }}
                onBlur={() => setTimeout(() => setFavOpen(false), 150)}
                className={campo} />
              {favOpen && party.trim().length >= 2 && (
                <ul className="neu-pop absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg glass-popover">
                  {favSugs.length === 0 && (
                    <li className="px-3 py-2 text-xs text-muted-foreground">Nenhum favorecido com esse nome.</li>
                  )}
                  {favSugs.map(f => (
                    <li key={f.id}>
                      {/* onMouseDown previne o blur do input antes do onClick disparar */}
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickFav(f)}
                        title={f.document ? `Selecionar (documento ${f.document})` : 'Selecionar favorecido'}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-secondary">
                        <span className="block text-foreground truncate">{f.name}</span>
                        {f.document && <span className="block text-xs text-muted-foreground">{f.document}</span>}
                      </button>
                    </li>
                  ))}
                  {/* Cadastrar fica SEMPRE no fim, mesmo havendo resultados: o
                      parecido não é o certo ("Padaria Sul" × "Padaria Sul Ltda")
                      e obrigar a apagar letras pra forçar a lista vazia é ruim. */}
                  <li className={favSugs.length > 0 ? 'border-t border-border' : ''}>
                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={openFavForm}
                      title="Cadastrar este favorecido agora, sem perder o lançamento"
                      className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-secondary inline-flex items-center gap-2">
                      <FiPlus className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">Cadastrar &ldquo;{party.trim()}&rdquo;</span>
                    </button>
                  </li>
                </ul>
              )}
            </div>
            <div>
              <label className={rotulo}>Valor*</label>
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} className={campo} placeholder="0,00" />
            </div>
          </div>
      </section>

      <section className="pln-secao">
          <h4 className="pln-secao-titulo">Datas e classificação</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={rotulo}>Competência*</label>
              <input type="date" value={competence} onChange={e => setCompetence(e.target.value)} className={campo} />
            </div>
            <div>
              <label className={rotulo}>Vencimento*</label>
              <input type="date" value={due} onChange={e => setDue(e.target.value)} className={campo} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <label className={rotulo}>Categoria*</label>
              {/* Combobox pesquisável: fechado mostra o nome da selecionada;
                  focar abre a lista completa (query zera) e digitar filtra. */}
              <input ref={catInputRef} value={catOpen ? catQuery : selCatName} autoComplete="off"
                placeholder={catOpen ? 'Digite para filtrar…' : 'Selecione…'}
                title="Clique e digite para filtrar as categorias"
                onFocus={() => { setCatQuery(''); setCatOpen(true) }}
                onClick={() => { if (!catOpen) { setCatQuery(''); setCatOpen(true) } }}
                onChange={e => { setCatQuery(e.target.value); setCatOpen(true) }}
                onKeyDown={e => { if (e.key === 'Escape' && catOpen) { e.stopPropagation(); setCatOpen(false) } }}
                onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                className={`${campo} pr-8`} />
              {/* onMouseDown previne roubar o foco do input (senão o blur fecha
                  a lista antes do clique); com seleção o botão limpa, sem
                  seleção abre/fecha a lista completa.
                  Posicao ancorada no INPUT (bottom-0 + altura do campo), nao no
                  topo do wrapper: o antigo top-[31px] media a partir do label e
                  so acertava enquanto o label ocupasse exatamente 24px. */}
              <button type="button" onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  if (categoryId) { setCategoryId(''); setCatQuery(''); catInputRef.current?.focus() }
                  else if (catOpen) setCatOpen(false)
                  else catInputRef.current?.focus()
                }}
                title={categoryId ? 'Limpar categoria' : 'Abrir lista de categorias'}
                className="absolute right-2 bottom-0 h-9 flex items-center p-1 rounded text-muted-foreground hover:text-foreground">
                {categoryId ? <FiX className="w-4 h-4" />
                            : <FiChevronDown className={`w-4 h-4 transition-transform ${catOpen ? 'rotate-180' : ''}`} />}
              </button>
              {catOpen && (
                <ul className="neu-pop absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg glass-popover">
                  {catFiltered.length === 0 && (
                    <li className="px-3 py-2 text-sm text-muted-foreground">Nenhuma categoria encontrada</li>
                  )}
                  {catFiltered.map(c => (
                    <li key={c.id}>
                      <button type="button" onMouseDown={e => e.preventDefault()}
                        onClick={() => { setCategoryId(String(c.id)); setCatOpen(false) }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary ${String(c.id) === categoryId ? 'text-primary font-medium' : 'text-foreground'}`}>
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label className={rotulo}>Centro de custo</label>
              <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)} className={campo}>
                <option value="">—</option>
                {ccs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
      </section>

      {/* Rateio: seção própria (só despesa). Junta o modelo da categoria e o
          quadro de partes — antes eram duas caixas soltas no meio dos campos. */}
      {kind === 'despesa' && (
      <section className="pln-secao">
          <h4 className="pln-secao-titulo">Rateio por categoria</h4>
          {/* Modelo de rateio da categoria + histórico do que já foi rateado
              nela (2026-07-30). A condição é `opens_split === 1` PURA, sem o
              `pareceCartao`: aquela regex é rede de segurança pro quadro de
              rateio não regredir, mas aqui se CADASTRA na categoria, e abrir
              isso para algo que só PARECE cartão convidaria a criar modelo em
              categoria que nem tem o flag.
              Desde 2026-10-16 monta em QUALQUER despesa com categoria, mas fora
              do opens_split só aparece se a categoria já tiver modelo. */}
          {kind === 'despesa' && !!categoryId && (
            <RateioCategoriaBox
              ocultarSeVazio={catSel?.opens_split !== 1}
              categoryId={Number(categoryId)}
              categoryName={selCatName}
              cats={cats}
              totalLancamento={totalLancamento}
              modeloVersao={modeloVersao}
              temSplitsAtuais={splits.length > 0}
              onModeloCount={setModeloCount}
              onAplicar={rows => { setSplits(rows); setSplitsOpen(true) }}
            />
          )}
          {/* Rateio — abre sozinho quando a categoria é de cartão, mas fica
              disponível pra qualquer despesa. O valor da fatura continua sendo
              o do lançamento; aqui se diz DE QUE ele é composto. */}
          {!rateioAberto && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <button type="button"
                onClick={() => { setSplitsOpen(true); setSplits([{ uid: novoUid(), category_id: '', amount: '', description: '' }]) }}
                className={chip}>
                <FiPlus className="w-4 h-4" /> Dividir em partes
              </button>
              <span className="text-[13px] text-muted-foreground">Este pagamento junta despesas de categorias diferentes?</span>
            </div>
          )}
          {/* O quadro de partes era uma caixa (neu-convex) dentro da gaveta;
              agora a seção já é a caixa, então aqui fica só o conteúdo. */}
          {rateioAberto && (
            <div>
              {/* Título e texto em CIMA, botões numa fileira própria embaixo.
                  Eram tudo a mesma linha flex até 2026-07-30, o que já era
                  apertado com 2 chips e quebrou de vez com o 3º ("Salvar como
                  modelo"): o drawer é max-w-lg (512px) e os chips sozinhos
                  passam de 400px, sem espaço pro texto ao lado. */}
              <div className="mb-3">
                {pareceCartao && !splits.length && (
                  <p className="text-[13px] text-muted-foreground">
                    {selCatName ? `"${selCatName}" costuma ser um pagamento único composto de vários itens. ` : ''}
                    Diga do que este valor é composto para o resultado sair nas categorias certas.
                  </p>
                )}
                {!pareceCartao && (
                  <p className="text-[13px] text-muted-foreground">
                    Diga quanto deste valor vai para cada categoria. O que não for dividido continua em {selCatName ? `“${selCatName}”` : 'na categoria do lançamento'}.
                  </p>
                )}
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => { setPasteOpen(o => !o); setPasteMsg('') }}
                    title="Colar a lista de categorias e valores direto do Excel"
                    className={chip}>
                    <FiClipboard className="w-4 h-4" /> Colar do Excel
                  </button>
                  <button type="button" onClick={() => { setSplitsOpen(true); setSplits(p => [...p, { uid: novoUid(), category_id: '', amount: '', description: '' }]) }}
                    className={chip}>
                    <FiPlus className="w-4 h-4" /> Adicionar parte
                  </button>
                  {/* Desistir só enquanto nada foi preenchido: com parte já
                      digitada, apagar é pelo ✕ de cada linha (sem perder
                      trabalho num clique). Lista vazia na edição continua
                      sendo "apaguei o rateio", igual a antes. */}
                  {!pareceCartao && splits.every(s => !s.category_id && !valorParte(s.amount)) && (
                    <button type="button" onClick={() => { setSplits([]); setSplitsOpen(false); setPasteOpen(false) }}
                      className={chip}>
                      <FiX className="w-4 h-4" /> Não dividir
                    </button>
                  )}
                  {!!categoryId && (
                    <button type="button" disabled={!splits.length || modeloSalvando}
                      onClick={() => (modeloCount > 0 ? setConfirmModelo(true) : salvarComoModelo())}
                      title={modeloCount > 0
                        ? `Substitui as ${modeloCount} partes já salvas no modelo desta categoria`
                        : 'Guarda estas partes no modelo da categoria, para os próximos meses'}
                      className={chip}>
                      {modeloSalvando ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />} Salvar como modelo
                    </button>
                  )}
                </div>
              </div>
              {modeloMsg && <p className="text-[13px] text-muted-foreground mb-2">{modeloMsg}</p>}

              {/* Aviso ATIVO de lançamento já conciliado. Diz a verdade em vez
                  de inventar efeito: a conciliação amarra o VALOR TOTAL do
                  título à linha do extrato (tabela reconciliation) e não
                  conhece as partes — verificado em 2026-07-30 editando e
                  apagando partes da txn 27933, a linha de conciliação ficou
                  idêntica. Mexer no rateio muda só a divisão por categoria. */}
              {editing && conc?.conciliado && (
                <p className="mb-2 flex items-start gap-1.5 text-[13px] text-emerald-700 dark:text-emerald-400"
                  title="A conciliação amarra o valor TOTAL do lançamento à linha do extrato. As partes só dividem esse valor por categoria no DRE e no Fluxo de Caixa — mexer nelas não desfaz nem invalida a conciliação.">
                  <FiCheck className="w-3.5 h-3.5 mt-px shrink-0" />
                  <span>Conciliado com o extrato{conc.em ? ` em ${conc.em.split('-').reverse().join('/')}` : ''} — mexer nas partes não desfaz a conciliação.</span>
                </p>
              )}

              {pasteOpen && (
                <div className="neu-convex mb-3 rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="text-[13px] text-muted-foreground mb-1.5">
                    Cole do Excel: uma linha por parte — categoria e valor separados por tab (as duas colunas). Substitui as partes atuais.
                  </p>
                  <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={5}
                    placeholder={'Combustível\t150,00\nAlimentação\t89,90\nMaterial de escritório\t45,00'}
                    className={`${inputCls} text-[13px] font-mono`} />
                  <div className="flex items-center gap-2 mt-2">
                    <button type="button" onClick={aplicarColagem} disabled={!pasteText.trim()}
                      className="neu-relief h-9 text-sm font-medium rounded-lg bg-primary text-primary-foreground px-4 hover:opacity-90 disabled:opacity-50">Processar</button>
                    <button type="button" onClick={() => { setPasteOpen(false); setPasteText('') }}
                      className={chip}>Cancelar</button>
                  </div>
                </div>
              )}
              {pasteMsg && <p className="mb-2 text-[13px] text-muted-foreground">{pasteMsg}</p>}

              {/* Uma CAIXA por parte, com categoria+valor em cima e a descrição
                  embaixo (2026-07-30). Antes era uma linha só e a descrição
                  NÃO tinha campo nenhum — ela só alimentava o placeholder do
                  combobox. Isso escondia justamente o caso do "Colar do Excel":
                  quando o nome colado não casa com nenhuma categoria, ele vai
                  para a descrição e a parte fica "(sem categoria)" — o operador
                  via o problema mas não via QUAL item era, nem tinha onde
                  corrigir o texto. A borda âmbar marca as que faltam
                  classificar. */}
              {splits.map((r, i) => {
                // A descrição aparece quando já tem texto, quando a parte está
                // sem categoria (aí ela é a única pista do que é aquilo — o
                // "Colar do Excel" joga o nome não reconhecido nesse campo), ou
                // quando o usuário abre pelo botão.
                const uid = r.uid || String(i)
                const mostraDesc = !!r.description || !r.category_id || descAberta.has(uid)
                // O botão só existe quando dá para ALTERNAR. Com texto escrito
                // ou sem categoria a descrição é obrigatória na tela, e um
                // botão "Ocultar" ali não fazia nada — mentia sobre o que faz.
                const podeAlternar = !r.description && !!r.category_id
                return (
                  <div key={uid}
                    /* p-1.5 em TODAS as linhas e só a cor da borda muda: dar
                       padding apenas à linha âmbar encolhia ela em 7px e as
                       colunas de valor/botões saíam do alinhamento das outras. */
                    className={`mb-1.5 rounded-md border p-1.5 ${r.category_id ? 'border-transparent' : 'border-amber-500/40 bg-amber-500/5'}`}>
                    <div className="flex gap-2 items-start">
                      <SplitCatSelect value={r.category_id} options={cats}
                        hint={r.description || undefined} fallbackName={r.category_name}
                        onChange={id => setSplits(p => p.map((x, j) => j === i ? { ...x, category_id: id, category_name: undefined } : x))} />
                      <input value={r.amount} inputMode="decimal" placeholder="0,00"
                        aria-label={`Valor da parte ${i + 1}`} title="Valor desta parte do rateio. Dá para somar: 50,00+300+305,99 e Enter"
                        onChange={e => setSplits(p => p.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = resolverConta(r.amount); setSplits(p => p.map((x, j) => j === i ? { ...x, amount: v } : x)) } }}
                        onBlur={() => { const v = resolverConta(r.amount); if (v !== r.amount) setSplits(p => p.map((x, j) => j === i ? { ...x, amount: v } : x)) }}
                        className={`${inputBase} h-9 w-24 shrink-0 text-right`} />
                      {/* O botão só faz sentido quando dá para ALTERNAR: com texto
                          escrito ou sem categoria a descrição é obrigatória na tela.
                          Nesses casos entra um ESPAÇADOR do mesmo tamanho — sem ele
                          o combobox cresce 40px e a linha sai do alinhamento das
                          outras (medido: valor em x=1225 numa, x=1265 noutra). */}
                      {podeAlternar ? (
                        <button type="button"
                          title={mostraDesc ? 'Ocultar a descrição' : 'Escrever uma descrição para esta parte'}
                          onClick={() => setDescAberta(prev => {
                            const n = new Set(prev)
                            if (n.has(uid)) n.delete(uid); else n.add(uid)
                            return n
                          })}
                          className="neu-convex w-8 shrink-0 self-stretch rounded-md border border-border flex items-center justify-center hover:bg-secondary text-muted-foreground">
                          <FiAlignLeft className="w-4 h-4" />
                        </button>
                      ) : <span className="w-8 shrink-0" aria-hidden="true" />}
                      <button type="button" title="Remover parte"
                        onClick={() => { setSplits(p => p.filter((_, j) => j !== i)); setDescAberta(prev => { const n = new Set(prev); n.delete(uid); return n }) }}
                        className="neu-convex w-8 shrink-0 self-stretch rounded-md border border-border text-muted-foreground hover:text-red-500 hover:bg-red-500/10 flex items-center justify-center">
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                    {mostraDesc && (
                      <input value={r.description}
                        aria-label={`Descrição da parte ${i + 1}`}
                        placeholder={r.category_id ? 'Descrição (opcional)' : 'Descrição — o que é esta parte?'}
                        onChange={e => setSplits(p => p.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                        className={`${campo} mt-1.5 text-[13px]`} />
                    )}
                  </div>
                )
              })}

              {splits.length > 0 && (
                <div className={`mt-1 text-[13px] rounded-lg px-3 py-2 ${
                  Math.abs(sobra) < 0.005 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                  : sobra < 0 ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
                  {sobra < 0
                    ? `O rateio passou R$ ${Math.abs(sobra).toFixed(2).replace('.', ',')} do valor do lançamento.`
                    : Math.abs(sobra) < 0.005
                      ? 'Rateio fecha com o valor do lançamento.'
                      : `Faltam R$ ${sobra.toFixed(2).replace('.', ',')} para fechar — a sobra fica na categoria do próprio lançamento.`}
                </div>
              )}
              {/* A soma pode fechar E ainda haver parte sem categoria (ela conta no
                  total) — nesse caso o valor vaza pra categoria do lançamento nos
                  relatórios. Avisa separado pra não passar batido no "fecha". */}
              {splits.some(s => !s.category_id) && (
                <div className="neu-relief mt-1.5 text-[13px] rounded-lg px-3 py-2 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                  {splits.filter(s => !s.category_id).length} parte(s) sem categoria — nos relatórios o valor fica na categoria do lançamento{selCatName ? ` (${selCatName})` : ''}. Selecione a categoria de cada uma.
                </div>
              )}
            </div>
          )}
      </section>
      )}

      <section className="pln-secao">
          <h4 className="pln-secao-titulo">Pagamento</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={rotulo}>Forma de pagamento</label>
              <select value={method} onChange={e => setMethod(e.target.value)} className={campo}>
                <option value="">—</option>
                {METHODS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            </div>
            <div>
              <label className={rotulo}>Conta</label>
              <select value={accountId} onChange={e => setAccountId(e.target.value)} className={campo}>
                <option value="">—</option>
                {accs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={rotulo}>Código de referência</label>
            <input value={reference} onChange={e => setReference(e.target.value)} className={campo} placeholder="Opcional" />
          </div>

          {!editing && !defaults?.lockPaid && (
            <div className="rounded-lg border border-border p-3">
              <label className={rotulo}>Repetir lançamento?</label>
              <div className="flex flex-wrap gap-2 text-sm">
                {([['none', 'Não'], ['installments', 'Parcelar valor'], ['recurrence', 'Repetir valor']] as const).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setRepeatMode(v)}
                    className={`h-9 px-3 rounded-lg border ${repeatMode === v ? 'border-primary text-primary bg-primary/5' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    {l}
                  </button>
                ))}
                {repeatMode !== 'none' && (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-muted-foreground">{repeatMode === 'installments' ? 'Parcelas' : 'Meses'}:</span>
                    <input type="number" min="2" max="120" value={repeatCount} onChange={e => setRepeatCount(Number(e.target.value))} className={`${inputBase} h-9 w-20`} />
                  </div>
                )}
              </div>
              {repeatMode !== 'none' && (
                <p className="text-[13px] text-muted-foreground mt-2">
                  {repeatMode === 'installments'
                    ? `O valor será dividido em ${Math.max(2, repeatCount)} parcelas mensais.`
                    : `Serão criados ${Math.max(2, repeatCount)} lançamentos mensais com o valor cheio.`}
                </p>
              )}
            </div>
          )}

          {defaults?.lockPaid ? (
            <p className="text-[13px] text-muted-foreground">
              A baixa ({kind === 'despesa' ? 'pagamento' : 'recebimento'}) será feita pela conciliação, com a data do movimento do extrato.
            </p>
          ) : (
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input type="checkbox" checked={paid} onChange={e => { setPaid(e.target.checked); if (e.target.checked) setScheduled(false) }} className="neu-check rounded border-border" />
              {kind === 'despesa' ? 'Já pago' : 'Já recebido'}
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input type="checkbox" checked={scheduled} onChange={e => { setScheduled(e.target.checked); if (e.target.checked) setPaid(false) }} className="neu-check rounded border-border" />
              Agendado
            </label>
          </div>
          )}
      </section>

      <section className="pln-secao">
          <h4 className="pln-secao-titulo">Observações e anexos</h4>
          <div>
            <label className={rotulo}>Observações</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={inputCls} />
          </div>

          {editing ? (
            <AttachmentsSection entityType="transaction" entityId={txn!.id} />
          ) : (
            <div>
              <label className={rotulo}>Anexos</label>
              <label
                className="neu-well flex items-center gap-2 h-10 rounded-lg border border-dashed border-border px-3 text-sm text-muted-foreground cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors"
                title="Anexar foto da fatura, boleto, nota fiscal… (até 20 MB por arquivo; sobem junto com o lançamento)"
              >
                <FiPaperclip className="w-4 h-4 shrink-0" />
                <span>Anexar arquivo (fatura, boleto, nota…)</span>
                <input
                  type="file" multiple className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files || [])
                    if (files.length) setPendingFiles(prev => [...prev, ...files])
                    e.target.value = '' // permite re-escolher o mesmo arquivo
                  }}
                />
              </label>
              {pendingFiles.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {pendingFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-2 text-[13px]">
                      <FiPaperclip className="w-3 h-3 shrink-0 text-muted-foreground" />
                      <span className="truncate flex-1 min-w-0 text-foreground">{f.name}</span>
                      <span className="text-muted-foreground shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                        title="Remover anexo" className="neu-relief p-0.5 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-500 transition-colors">
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
      </section>
      </>}
      {/* Substituir o modelo da categoria é destrutivo e irreversível: o PUT
          em lote apaga as partes salvas e regrava as deste lançamento como
          VALOR FIXO. Um modelo em percentual (reutilizável em qualquer mês)
          viraria valores de um mês só. */}
      <ConfirmDialog
        isOpen={confirmModelo}
        portal zIndex={100050} closeOnEsc
        type="warning"
        title="Substituir o modelo da categoria"
        message={`O modelo de “${selCatName}” já tem ${modeloCount} parte(s) salvas. Elas serão apagadas e substituídas pelas ${splits.length} partes deste lançamento.`}
        details={[
          'As partes entram no modelo como VALOR FIXO — se havia percentuais salvos, eles se perdem.',
          'Isto não altera nenhum lançamento, só o modelo que a categoria oferece nos próximos.',
        ]}
        confirmText="Substituir modelo"
        onConfirm={salvarComoModelo}
        onCancel={() => setConfirmModelo(false)}
      />
    </SideModal>
  )
}
